import React, { useMemo } from 'react'
import { Link, matchPath, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { Icon as AbwIcon } from '@Components/AbwaabPlus'
import routes from '@Constants/Routes'
import usePlatformFlags from '@Hooks/usePlatformFlags'

type NavKey = 'home' | 'classrooms' | 'explore' | 'activity' | 'account'

type NavItem = {
	key: NavKey
	icon: string
	labelKey: string
	fallback: string
	to: string
	/** Route patterns that light this tab up (design's own grouping). */
	active: string[]
}

/* فصولي owns the classroom/course surfaces; استكشف owns discovery pages —
   mirrors the design template's TABS route grouping. Old /dashboard/* paths
   stay mapped until their phases migrate them. Always 5 tabs in every
   platform mode; courses-only just relabels فصولي → كورساتي. */
const buildItems = (coursesEnabled: boolean, coursesOnly: boolean): NavItem[] => [
	{ key: 'home', icon: 'home', labelKey: 'nav.home', fallback: 'الرئيسية', to: '/', active: ['/'] },
	{
		key: 'classrooms',
		icon: 'book-open',
		labelKey: coursesOnly ? 'nav.courses' : 'nav.classrooms',
		fallback: coursesOnly ? 'كورساتي' : 'فصولي',
		to: routes.classrooms,
		active: [
			routes.classrooms,
			'/classroom/:label/*',
			'/classroom/:label',
			'/dashboard/classrooms/*',
			routes.test,
			// standalone details + player light the my-learning tab like classrooms
			...(coursesEnabled ? ['/courses/:label', '/courses/:label/*'] : []),
		],
	},
	{
		key: 'explore',
		icon: 'compass',
		labelKey: 'nav.explore',
		fallback: 'استكشف',
		to: '/search',
		active: ['/search', '/instructor/:label', '/results'],
	},
	{
		key: 'activity',
		icon: 'trending-up',
		labelKey: 'nav.activity',
		fallback: 'النشاط',
		to: routes.activity,
		active: [`${routes.activity}/*`, routes.activity, routes.wallet, routes.invoices, routes.timeline, '/dashboard/activity/*', '/dashboard/activity'],
	},
	{
		key: 'account',
		icon: 'user',
		labelKey: 'nav.account',
		fallback: 'حسابي',
		to: routes.account,
		active: [`${routes.account}/*`, routes.account, '/dashboard/profile', '/dashboard/settings', routes.dashboard],
	},
]

export type AppNavProps = {
	/** 'bottom' = the mobile fixed bar; 'header' = the desktop header row. */
	variant: 'bottom' | 'header'
}

/**
 * The logged-in navigation frame (design's 5 tabs). One source of truth for
 * items + active matching; rendered as a fixed bottom bar on mobile and as a
 * row inside the desktop header. Items are real links (keyboard, middle-click,
 * aria-current) styled as the design's pills.
 */
const AppNav: React.FC<AppNavProps> = ({ variant }) => {
	const [t] = useTranslation('layouts', { keyPrefix: 'header' })
	const { pathname } = useLocation()
	const { coursesEnabled, coursesOnly } = usePlatformFlags()
	const items = useMemo(() => buildItems(coursesEnabled, coursesOnly), [coursesEnabled, coursesOnly])

	const isActive = (item: NavItem) => item.active.some(p => !!matchPath(p, pathname))

	if (variant === 'header') {
		return (
			<HeaderRow aria-label="nav">
				{items.map(item => {
					const on = isActive(item)
					return (
						<HeaderItem key={item.key} to={item.to} data-active={on ? 'true' : undefined} aria-current={on ? 'page' : undefined}>
							<AbwIcon name={item.icon} size={18} stroke={on ? 2.5 : 2} />
							<span>{t(item.labelKey, { defaultValue: item.fallback })}</span>
						</HeaderItem>
					)
				})}
			</HeaderRow>
		)
	}

	return (
		<BottomBar aria-label="nav">
			{items.map(item => {
				const on = isActive(item)
				return (
					<BottomItem key={item.key} to={item.to} data-active={on ? 'true' : undefined} aria-current={on ? 'page' : undefined}>
						<AbwIcon name={item.icon} size={22} stroke={on ? 2.5 : 2} />
						<span>{t(item.labelKey, { defaultValue: item.fallback })}</span>
					</BottomItem>
				)
			})}
		</BottomBar>
	)
}

export default AppNav

/* Desktop header row — 44px pill links, active gets the blue-08 tint. */
const HeaderRow = styled.nav`
	display: flex;
	align-items: center;
	gap: 4px;
	flex: 1;
	min-width: 0;
`
const HeaderItem = styled(Link)`
	height: 44px;
	padding: 0 14px;
	border: none;
	border-radius: 10px;
	background: transparent;
	color: var(--text-secondary);
	cursor: pointer;
	text-decoration: none;
	display: flex;
	align-items: center;
	gap: 6px;
	font-family: var(--font-sans);
	white-space: nowrap;
	span {
		font: var(--fw-medium) 13px var(--font-sans);
	}
	&[data-active='true'] {
		background: var(--blue-08);
		color: var(--blue);
		span {
			font-weight: var(--fw-bold);
		}
	}
`

/* Mobile fixed bottom bar — 64px, borders top, five equal touch targets. */
const BottomBar = styled.nav`
	position: fixed;
	bottom: 0;
	right: 0;
	left: 0;
	z-index: 50;
	height: var(--bottomnav-height, 64px);
	background: var(--surface-card);
	border-top: 1px solid var(--border);
	display: flex;
	align-items: center;
	justify-content: space-around;
	@media (min-width: 1024px) {
		display: none;
	}
`
const BottomItem = styled(Link)`
	min-width: 64px;
	height: 44px;
	border: none;
	background: transparent;
	cursor: pointer;
	text-decoration: none;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 4px;
	color: var(--text-disabled);
	font-family: var(--font-sans);
	span {
		font: var(--fw-regular) 10px var(--font-sans);
	}
	&[data-active='true'] {
		color: var(--blue);
		span {
			font-weight: var(--fw-bold);
		}
	}
`
